import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Phone, Mail, MapPin, Building2, Star, ExternalLink } from 'lucide-react'
import { BottomNavigator } from '@/components/BottomNavigator'
import { CityFilter } from '@/components/CityFilter'
import { spacing } from '@/theme'
import { useResponsive } from '@/hooks/useResponsive'
import { api } from '@/api/axios'
import { useEffect, useState } from 'react'

type Ally = {
  id: string
  name: string
  image: string | null
  link: string | null
  address: string | null
  phones: string | null
  email: string | null
  city: string | null
  isActive: boolean
  companyType?: { id: string; name: string }
}

export function AliadosScreen() {
  const navigate = useNavigate()
  const { isDesktop } = useResponsive()
  const [allies, setAllies] = useState<Ally[]>([])
  const [loading, setLoading] = useState(true)
  const [cityFilter, setCityFilter] = useState('')

  useEffect(() => {
    api.get('/allies/active').then((res) => {
      setAllies(res.data.data || [])
    }).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const filtered = cityFilter ? allies.filter((a) => a.city === cityFilter) : allies

  return (
    <>
      <div
        style={{
          minHeight: '100vh',
          padding: spacing[4],
          paddingBottom: '56px',
          background: 'linear-gradient(180deg, #020617 0%, #0f172a 30%, #1e3a5f 60%, #0f172a 100%)',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <button
            onClick={() => navigate('/interacciones')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: spacing[2],
              color: '#a7f3d0',
              marginBottom: spacing[3],
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            <ArrowLeft size={18} />
            Volver
          </button>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              borderRadius: 24,
              padding: spacing[5],
              marginBottom: spacing[4],
              background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.15) 0%, rgba(6, 78, 59, 0.4) 100%)',
              border: '1px solid rgba(52, 211, 153, 0.3)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{
              position: 'absolute',
              top: -60,
              right: -60,
              width: 160,
              height: 160,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(52, 211, 153, 0.12) 0%, transparent 70%)',
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: spacing[3], position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: 16,
                background: 'linear-gradient(135deg, #34d399, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(52, 211, 153, 0.3)',
              }}>
                <Star size={24} color="#fff" />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: '#d1fae5' }}>Nuestros Aliados</h1>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6ee7b7' }}>Empresas y organizaciones que confian en nosotros</p>
              </div>
            </div>
          </motion.div>

          <div style={{ marginBottom: spacing[4] }}>
            <CityFilter value={cityFilter} onChange={setCityFilter} label="Filtrar por ciudad" />
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
              {[1, 2, 3].map((i) => (
                <div key={i} style={{ borderRadius: 20, padding: spacing[4], background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(52, 211, 153, 0.1)', height: 120, display: 'flex', alignItems: 'center', gap: spacing[4] }}>
                  <div style={{ width: 72, height: 72, borderRadius: 16, background: 'rgba(52, 211, 153, 0.1)' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ width: '60%', height: 14, borderRadius: 8, background: 'rgba(52, 211, 153, 0.1)', marginBottom: 8 }} />
                    <div style={{ width: '40%', height: 10, borderRadius: 8, background: 'rgba(52, 211, 153, 0.05)' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing[10], color: '#64748b' }}>
              <Building2 size={48} style={{ margin: '0 auto', opacity: 0.3 }} />
              <p style={{ marginTop: spacing[2], fontSize: 15 }}>{cityFilter ? 'No hay aliados en esta ciudad' : 'Proximamente...'}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
              {filtered.map((ally, idx) => (
                <motion.div
                  key={ally.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                  whileHover={ally.link ? { y: -4, scale: 1.01 } : {}}
                  whileTap={ally.link ? { scale: 0.99 } : {}}
                  onClick={() => { if (ally.link) window.open(ally.link, '_blank', 'noopener,noreferrer') }}
                  style={{
                    borderRadius: 20,
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(15, 23, 42, 0.8) 100%)`,
                    border: '1px solid rgba(52, 211, 153, 0.2)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                    backdropFilter: 'blur(10px)',
                    display: 'flex',
                    flexDirection: isDesktop ? 'row' : 'column',
                    cursor: ally.link ? 'pointer' : 'default',
                  }}
                >
                  {ally.image ? (
                    <div style={{
                      width: isDesktop ? 140 : '100%',
                      height: isDesktop ? 140 : 180,
                      flexShrink: 0,
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                      <img
                        src={ally.image}
                        alt={ally.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                      />
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        background: isDesktop ? 'linear-gradient(to right, transparent 60%, rgba(15, 23, 42, 0.9) 100%)' : 'linear-gradient(to top, transparent 60%, rgba(15, 23, 42, 0.9) 100%)',
                      }} />
                    </div>
                  ) : (
                    <div style={{
                      width: isDesktop ? 140 : '100%',
                      height: isDesktop ? 140 : 100,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.08), rgba(5, 150, 105, 0.15))',
                    }}>
                      <Building2 size={40} color="#34d399" style={{ opacity: 0.4 }} />
                    </div>
                  )}
                  <div style={{
                    flex: 1,
                    padding: spacing[4],
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: '6px',
                    minWidth: 0,
                  }}>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
                      {ally.name}
                      {ally.link && <ExternalLink size={14} color="#34d399" />}
                    </h3>
                    {ally.companyType && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '3px 10px',
                        borderRadius: 999,
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#d1fae5',
                        background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.15), rgba(5, 150, 105, 0.1))',
                        border: '1px solid rgba(52, 211, 153, 0.25)',
                        width: 'fit-content',
                      }}>
                        <Building2 size={12} /> {ally.companyType.name}
                      </span>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing[2], marginTop: spacing[1], fontSize: 12, color: '#94a3b8' }}>
                      {ally.phones && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Phone size={13} color="#34d399" /> {ally.phones}
                        </span>
                      )}
                      {ally.email && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Mail size={13} color="#34d399" /> {ally.email}
                        </span>
                      )}
                    </div>
                    {ally.address && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b', marginTop: '2px' }}>
                        <MapPin size={13} color="#34d399" /> {ally.address}
                      </span>
                    )}
                    {ally.city && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#6ee7b7', marginTop: '2px' }}>
                        <MapPin size={13} color="#34d399" /> {ally.city}
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNavigator />
    </>
  )
}
